import { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { api } from '../lib/api'
import type { EntityType } from '../types'
import { ENTITY_COLORS } from '../lib/graph-utils'

type CreateType = 'person' | 'project' | 'task' | 'ai_note'

interface CreateModalProps {
  isOpen: boolean
  type: CreateType
  onClose: () => void
  onSuccess: () => void
}

export default function CreateModal({ isOpen, type, onClose, onSuccess }: CreateModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [context, setContext] = useState('')
  const [priority, setPriority] = useState(5)
  const resetForm = () => {
    setName('')
    setDescription('')
    setContext('')
    setPriority(5)
    setError(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      let res
      switch (type) {
        case 'person':
          res = await api.createPerson({ name, context: context || undefined })
          break
        case 'project':
          res = await api.createProject({ name, description: description || undefined })
          break
        case 'task':
          res = await api.createTask({ title: name, description: description || undefined, priority })
          break
        case 'ai_note':
          res = await api.createNote(description)
          break
      }

      if (res) {
        onSuccess()
        handleClose()
      } else {
        setError('Failed to create')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  const color = ENTITY_COLORS[type as EntityType]
  const title = {
    person: 'Add Person',
    project: 'Add Project',
    task: 'Add Task',
    ai_note: 'Add AI Note',
  }[type]

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: `${color}40`, backgroundColor: `${color}10` }}
        >
          <h2 className="text-lg font-medium text-white">{title}</h2>
          <button onClick={handleClose} className="p-1 hover:bg-white/10 rounded">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name/Title field */}
          {type !== 'ai_note' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                {type === 'task' ? 'Title' : 'Name'}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-gray-600"
                placeholder={type === 'person' ? 'John Doe' : type === 'project' ? 'My Project' : 'Task title'}
              />
            </div>
          )}

          {/* Context for person */}
          {type === 'person' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Context (optional)</label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-gray-600 resize-none"
                placeholder="Who is this person?"
              />
            </div>
          )}

          {/* Description for project/task/ai_note */}
          {(type === 'project' || type === 'task' || type === 'ai_note') && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                {type === 'ai_note' ? 'Content' : 'Description'} {type !== 'ai_note' && '(optional)'}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required={type === 'ai_note'}
                rows={4}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-gray-600 resize-none"
                placeholder={type === 'ai_note' ? 'AI insight or note content...' : 'Describe it...'}
              />
            </div>
          )}

          {/* Priority for task */}
          {type === 'task' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Priority (1-10)</label>
              <input
                type="range"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="text-center text-sm text-gray-500">P{priority}</div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-3 py-2 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: color, color: '#000' }}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
